import React, { useEffect } from 'react';
import GameInfoPanel from '../GameInfoPanel';
import SoloStatusWide from '../GameInfoPanel/SoloStatusWide';
import SoloStatusNarrow from '../GameInfoPanel/SoloStatusNarrow';
import CreationStagePanel from '../CreationStagePanel';
import ValueSelectorWide from '../ValueSelector/ValueSelectorWide';
import ValueSelectorNarrow from '../ValueSelector/ValueSelectorNarrow';
import ActionPanelWide from '../ActionPanel/ActionPanelWide';
import ActionPanelNarrow from '../ActionPanel/ActionPanelNarrow';
import GridBoard from '../GridBoard/GridBoard';
import OpponentPanel from '../OpponentPanel';
import styles from './GameContainer.module.css';
import { useGameState } from '../../context/GameStateContext';
import { usePuzzleState } from '../../context/PuzzleStateContext';
import { useResponsiveLayout } from '../../hooks/useResponsiveLayout';
import { useClock } from '../../hooks/useClock';

const GameContainer = () => {
  

  
  // Game state context for high-level operations
  const { activeMode, gameData } = useGameState();
  
  // Puzzle state context for puzzle-specific operations
  const { 
    puzzleState, 
    optimisticGrid, 
    selectedCell, 
    makeMove,
    setNotes,
    isLoading,
    isNoteMode,
    setIsNoteMode
  } = usePuzzleState();
  
  // Initialize responsive layout calculations and get layout state
  const { isNarrow, cssLoaded } = useResponsiveLayout();

  useEffect(() => {
    // Set a CSS variable for grid size that we can use in our styles
    if (gameData?.puzzle?.size) {
      document.documentElement.style.setProperty('--grid-size', gameData.puzzle.size);
    }
  }, [gameData?.puzzle?.size]);

  const clockTime = useClock(puzzleState?.state?.clock_time);

  // Early return AFTER all hooks are called
  // Show loading state while CSS is being loaded to prevent FOUC
  if (!cssLoaded) {
    return <div className={styles.gameContainer} data-game-container="true">
      <div className={styles.error}>Loading...</div>
    </div>;
  }

  // Check if we have either the puzzle state grid OR an optimistic grid
  const hasGridData = (puzzleState?.grid && puzzleState.grid.allowed_values) || optimisticGrid;

  if (!puzzleState || !hasGridData) {
    return <div className={styles.error}>Loading game data...</div>;
  }

  // Use optimistic grid from context
  const grid = optimisticGrid || puzzleState.grid;

  // Check creation stage for ValueSelector visibility
  const creationStage = puzzleState?.state?.creation_stage;
  const showValueSelector = activeMode !== 'create' || creationStage === 1;

  // Calculate existing notes for the selected cell
  const currentCell = selectedCell 
    ? grid.cells.find(cell => cell.x === selectedCell.x && cell.y === selectedCell.y)
    : null;
  
  // Get notes from either the cell's footer_text or from the backend state.cell_notes
  let existingNotes = [];
  if (selectedCell) {
    // First check if we have notes in the backend state
    const notesKey = `${selectedCell.x},${selectedCell.y}`;
    if (puzzleState?.state?.cell_notes?.[notesKey]) {
      existingNotes = puzzleState.state.cell_notes[notesKey];
    } else if (currentCell?.footer_text) {
      // Fallback to cell's footer_text
      existingNotes = currentCell.footer_text;
    }
  }

  // Handle value selection from ValueSelector
  const handleValueSelect = async (value) => {
    if (!selectedCell) return;
    await makeMove(selectedCell.x, selectedCell.y, value);
  };

  // Handle note selection - backend handles toggle logic
  const handleNoteSelect = async (value) => {
    if (!selectedCell) return;
    
    // Backend expects a single value and handles the toggle
    await setNotes(selectedCell.x, selectedCell.y, value.toString());
    // Don't deselect the cell for notes
  };

  return (
    <div className={styles.gameContainer} data-game-container="true">
      <div className={styles.playArea}>
        {/* Top (mode) panel */}
        <div className={styles.modePanel}>
          {activeMode === 'create' && <CreationStagePanel />}
          
          {activeMode === 'solo' && (
            // Solo mode - use responsive status panels
            isNarrow ? <SoloStatusNarrow /> : <SoloStatusWide />
          )}
          
          {activeMode === 'duel' && <OpponentPanel clockTime={clockTime} />}
        </div>

        {isNarrow ? (
          // Narrow layout: board only in playRow, panels below in narrowStack
          <>
            <div className={styles.playRowNarrow}>
              <section className={`${styles.gridBoard}`}>
                <GridBoard />
              </section>
            </div>

            {/* Narrow-only bottom bars */}
            <div className={`${styles.narrowStack} ${!showValueSelector ? styles.narrowStackSingle : ''}`}>
              {showValueSelector && (
                <div className={`${styles.valuePanel} ${styles.panelNarrow}`}>
                  <ValueSelectorNarrow 
                    allowedValues={grid.allowed_values}
                    onValueSelect={handleValueSelect}
                    onNoteSelect={handleNoteSelect}
                    selectedCell={selectedCell}
                    loading={isLoading}
                    currentNotes={existingNotes}
                    showNotes={isNoteMode}
                    onToggleNotes={setIsNoteMode}
                  />
                </div>
              )}
              <div className={`${styles.actionPanel} ${styles.panelNarrow}`}>
                <ActionPanelNarrow 
                  actions={puzzleState?.grid?.allowed_actions || []}
                  allowedOperations={puzzleState?.grid?.allowed_operations || []}
                  allowedColors={puzzleState?.grid?.allowed_colors || []}
                  loading={isLoading}
                  selectedCell={selectedCell}
                />
              </div>
            </div>
          </>
        ) : (
          // Wide layout: value | board | action in playRow
          <div className={styles.playRow}>
            <aside className={`${styles.valuePanel} ${styles.panelWide} ${!showValueSelector ? styles.hidden : ''}`}>
              <ValueSelectorWide 
                allowedValues={grid.allowed_values}
                onValueSelect={handleValueSelect}
                onNoteSelect={handleNoteSelect}
                selectedCell={selectedCell}
                loading={isLoading}
                currentNotes={existingNotes}
                showNotes={isNoteMode}
                onToggleNotes={setIsNoteMode}
              />
            </aside>
            <section className={`${styles.gridBoard}`}>
              <GridBoard />
            </section>
            <aside className={`${styles.actionPanel} ${styles.panelWide}`}>
              <ActionPanelWide 
                actions={puzzleState?.grid?.allowed_actions || []}
                allowedOperations={puzzleState?.grid?.allowed_operations || []}
                allowedColors={puzzleState?.grid?.allowed_colors || []}
                loading={isLoading}
                selectedCell={selectedCell}
              />
            </aside>
          </div>
        )}
      </div>
    </div>
  );
};

export default GameContainer;
